import moment from 'moment';
import type { IntlShape } from 'react-intl';

import type {
  ScheduleComplianceRangeKey,
  ScheduleDrilldownTarget,
} from '@src/models/interfaces/executionsHistoryDashboard.interface';
import {
  COMPLIANCE_RANGES,
  getComplianceColor,
  getComplianceScaleColor,
} from '@src/packages/dashboard/components/ReportSummary/utils/compliance';

import type { ScheduleDashboardDateType } from '../../types';
import type { ScheduleSummaryPeriodPoint } from './types';
import { displayProgressPercentage } from '@src/packages/utils/functions/triggers/progress';

export const SCHEDULE_PRIMARY_COLOR = '#3f8dd8';
export const SCHEDULE_RESULT_COLORS = ['#52c41a', '#faad14', '#ff4d4f'];
export const SCHEDULE_EXECUTION_COLORS = ['#d9d9d9', SCHEDULE_PRIMARY_COLOR];
export const SCHEDULE_DIVISION_COLORS = [
  '#3478C9',
  '#4A90D9',
  '#2563A6',
  '#6B8FD6',
  '#F2994A',
  '#8B7BD8',
  '#5B9BD5',
  '#7A9CC6',
];

const RANGE_FILTER_BY_KEY: Readonly<Record<string, ScheduleComplianceRangeKey>> = {
  '100': '100',
  '85-99': '85-99',
  '70-84': '70-84',
  '50-69': '50-69',
  '1-49': '1-49',
  '0': '0',
  'range-100': '100',
  'range-85-99': '85-99',
  'range-70-84': '70-84',
  'range-50-69': '50-69',
  'range-1-49': '1-49',
  'range-0': '0',
  'schedule-range-100': '100',
  'schedule-range-85-99': '85-99',
  'schedule-range-70-84': '70-84',
  'schedule-range-50-69': '50-69',
  'schedule-range-1-49': '1-49',
  'schedule-range-0': '0',
  'event-range-100': '100',
  'event-range-85-99': '85-99',
  'event-range-70-84': '70-84',
  'event-range-50-69': '50-69',
  'event-range-1-49': '1-49',
  'event-range-0': '0',
  'user-range-100': '100',
  'user-range-85-99': '85-99',
  'user-range-70-84': '70-84',
  'user-range-50-69': '50-69',
  'user-range-1-49': '1-49',
  'user-range-0': '0',
};

const SCHEDULE_METRIC_FILTER_BY_KEY: Readonly<Record<string, 'all' | 'completed' | 'partial' | 'inactive'>> = {
  'metric-schedules': 'all',
  'metric-completed': 'completed',
  'metric-partial': 'partial',
  'metric-inactive': 'inactive',
} as const;

const USER_METRIC_FILTER_BY_KEY: Readonly<Record<string, 'assigned' | 'active' | 'complete' | 'partial' | 'inactive'>> = {
  'metric-assigned': 'assigned',
  'metric-active': 'active',
  'metric-complete': 'complete',
  'metric-partial': 'partial',
  'metric-inactive': 'inactive',
} as const;

export const getComplianceRangeFilter = (key: string) => RANGE_FILTER_BY_KEY[key];

export const getComplianceRangeColor = (key: string): string => {
  const filter = getComplianceRangeFilter(key);
  return COMPLIANCE_RANGES.find(range => range.key === filter)?.color
    ?? getComplianceScaleColor(1);
};

export const getDivisionComplianceColor = getComplianceScaleColor;
export { getComplianceColor };

export function getScheduleDrilldownTarget(kind: 'schedules', key: string): Extract<ScheduleDrilldownTarget, { kind: 'schedules' }> | null;
export function getScheduleDrilldownTarget(kind: 'eventTypes', key: string): Extract<ScheduleDrilldownTarget, { kind: 'eventTypes' }> | null;
export function getScheduleDrilldownTarget(kind: 'users', key: string): Extract<ScheduleDrilldownTarget, { kind: 'users' }> | null;
export function getScheduleDrilldownTarget(
  kind: ScheduleDrilldownTarget['kind'],
  key: string,
): ScheduleDrilldownTarget | null {
  if (kind === 'eventTypes') {
    const filter = RANGE_FILTER_BY_KEY[key];
    return filter ? { kind, filter } : null;
  }

  if (kind === 'schedules') {
    const rangeFilter = RANGE_FILTER_BY_KEY[key];
    if (rangeFilter) return { kind, filter: rangeFilter };
    const metricFilter = SCHEDULE_METRIC_FILTER_BY_KEY[key];
    return metricFilter ? { kind, filter: metricFilter } : null;
  }

  const rangeFilter = RANGE_FILTER_BY_KEY[key];
  if (rangeFilter) return { kind, filter: rangeFilter };
  const metricFilter = USER_METRIC_FILTER_BY_KEY[key];
  return metricFilter ? { kind, filter: metricFilter } : null;
}

export const MIN_CHART_PAGE_SIZE = 7;
export const MAX_CHART_PAGE_SIZE = 30;

const MIN_CHART_SLOT_WIDTH: Record<ScheduleDashboardDateType, number> = {
  day: 48,
  week: 90,
  month: 108,
  quarter: 112,
  year: 78,
};

export const getChartPageSize = (
  chartWidth: number,
  groupBy: ScheduleDashboardDateType,
): number => {
  if (chartWidth <= 0) {
    return MIN_CHART_PAGE_SIZE;
  }

  return Math.min(
    groupBy === 'day' ? MAX_CHART_PAGE_SIZE : 16,
    Math.max(MIN_CHART_PAGE_SIZE, Math.floor(chartWidth / MIN_CHART_SLOT_WIDTH[groupBy])),
  );
};

/** An empty scope represents the organization-wide selection (all divisions). */
export const shouldShowDivisionCompliance = (divisionIds: string[] = []): boolean =>
  divisionIds.length !== 1;

const capitalize = (value: string) =>
  value.length > 0 ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value;

export const formatPeriodLabel = (
  period: string,
  groupBy: ScheduleDashboardDateType,
  intl: IntlShape,
) => {
  if (groupBy === 'week') {
    return intl.formatMessage(
      { id: 'dashboards.schedules.summary.period.week' },
      { week: moment.utc(period).isoWeek() },
    );
  }

  if (groupBy === 'month') {
    return capitalize(
      intl.formatDate(moment.utc(period).toDate(), { timeZone: 'UTC', month: 'short', year: 'numeric' }).replace('.', ''),
    );
  }

  if (groupBy === 'quarter') {
    return intl.formatMessage(
      { id: 'dashboards.schedules.summary.period.quarter' },
      { quarter: moment.utc(period).quarter(), year: moment.utc(period).year() },
    );
  }

  if (groupBy === 'year') {
    return intl.formatDate(moment.utc(period).toDate(), { timeZone: 'UTC', year: 'numeric' });
  }

  const day = intl.formatDate(moment.utc(period).toDate(), { timeZone: 'UTC', day: '2-digit' });
  const month = intl
    .formatDate(moment.utc(period).toDate(), { timeZone: 'UTC', month: 'short' })
    .replace('.', '')
    .slice(0, 3);
  return `${day} ${capitalize(month)}`;
};

/** Derive the displayed fulfillment from counts, even when a legacy server rounded to 100. */
export const getSchedulePeriodCompletion = (point: ScheduleSummaryPeriodPoint): number =>
  displayProgressPercentage(point.expected > 0 ? point.completed / point.expected * 100 : 0);

/** Schedule charts count required/fulfilled events; routes retain execution-unit counts. */
export const getScheduleExecutionCounts = (point: ScheduleSummaryPeriodPoint, variant: 'schedules' | 'routes') => ({
  expected: variant === 'schedules' ? point.expected : point.executionExpected ?? point.expected,
  completed: variant === 'schedules' ? point.completed : point.executionCompleted ?? point.completed,
});
